import type { CSSProperties, ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Sortable.tsx — shared drag-reorder primitives (extracted from C.5).
 *
 * The C.5 ClientUpdatesTab established the pattern: dnd-kit + dedicated grip
 * handle + optimistic cache + per-row PATCH. This module factors the dnd-kit
 * boilerplate (sensors, DndContext, SortableContext, useSortable, grip SVG)
 * out of any specific surface so it can be reused on:
 *   - Tracked documents (Documents page) within each category section
 *   - Required items checklist inside the Permit modal
 *   - Future: phase task list (Build D), plans tracker (C.6), etc.
 *
 * Persistence is intentionally NOT in here — the caller decides what to do
 * with the reordered list (per-row PATCH, JSON-array swap, optimistic cache
 * update, etc.). This module is purely the UI layer.
 *
 * Activation distance: 6px on the pointer sensor, so a stationary click on
 * the grip handle (or a row that contains the handle) doesn't accidentally
 * start a drag. Keyboard sensor enabled for accessibility (Space to grab,
 * arrows to move, Space to drop).
 */

/* -------------------------------------------------------------------------- */
/*  SortableList — DndContext + SortableContext + sensors                     */
/* -------------------------------------------------------------------------- */

/**
 * Wrap a list of children in a sortable context. The caller provides:
 *   - `items`: the array of items (any T) — passes through arrayMove
 *   - `getId`: a function returning each item's stable string id
 *   - `onReorder`: called with the new T[] after a successful drop
 *
 * The caller is responsible for rendering the children (typically a `<ul>`
 * containing one `<SortableRow>` or one item-component-using-`useSortableRow`
 * per item) AND for whatever persistence the new order requires.
 */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  children,
}: {
  items: T[];
  getId: (item: T) => string;
  onReorder: (next: T[]) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = items.map(getId);

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/* -------------------------------------------------------------------------- */
/*  useSortableRow — hook returning the props the caller spreads on the row   */
/* -------------------------------------------------------------------------- */

/**
 * Returns the props needed to wire one row into the surrounding
 * `<SortableList>`. Spread `setNodeRef` + `style` on the row's outer
 * container. Spread `dragHandleProps` on a separate, dedicated grip element
 * (typically `<DragHandle>` from this module) — keeping the listeners on
 * a sub-element rather than the whole row prevents click conflicts with
 * row-as-button behaviors (edit-on-click, etc.).
 */
export function useSortableRow(id: string) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
    boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.12)' : undefined,
  };

  return {
    setNodeRef,
    style,
    isDragging,
    dragHandleProps: {
      ref: setActivatorNodeRef,
      ...attributes,
      ...listeners,
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  DragHandle — the visible grip button                                      */
/* -------------------------------------------------------------------------- */

/**
 * Visual grip-handle button. Spread `dragHandleProps` from `useSortableRow`
 * onto it. Renders the 6-dot SVG, `cursor: grab` / `active:cursor-grabbing`,
 * focus-ring matching the rest of the design language.
 *
 * `size="sm"` (default) is the chip-sized handle used in tight rows
 * (Required items). `size="md"` is the row-sized handle (Activity log,
 * Tracked documents).
 */
export function DragHandle({
  size = 'md',
  className = '',
  label = 'Drag to reorder',
  ...rest
}: {
  size?: 'sm' | 'md';
  className?: string;
  label?: string;
  // Forward attributes/listeners from useSortable. The ref is `setActivatorNodeRef`.
  ref?: (el: HTMLElement | null) => void;
  // Spreadable from useSortableRow().dragHandleProps:
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}) {
  const padding = size === 'sm' ? 'px-1.5' : 'px-2';
  // Static "tab" background — `bg-surface-2` (the established secondary
  // surface in the cream palette) gives the grip column a visible structure
  // even at rest, so the affordance reads as "this is a thing you can grab"
  // before the user hovers. Hover swaps to `bg-border` (slightly darker
  // neutral, also already in the design tokens) for clear contrast. Both
  // tones stay in the existing palette — no new colors introduced.
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`flex items-center justify-center ${padding} text-ink-2 hover:text-ink bg-surface-2 hover:bg-border cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-accent ${className}`}
      style={{ touchAction: 'none' }}
      {...rest}
    >
      <GripIcon size={size} />
    </button>
  );
}

/** 6-dot drag-handle icon, 2×3 grid. Tiny — pairs with rows that already
 *  carry full-weight content. */
export function GripIcon({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const w = size === 'sm' ? 8 : 10;
  const h = size === 'sm' ? 12 : 16;
  const r = size === 'sm' ? 1 : 1.25;
  const left = size === 'sm' ? 2 : 2;
  const right = size === 'sm' ? 6 : 8;
  const ys = size === 'sm' ? [3, 6, 9] : [3, 8, 13];
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
      focusable="false"
      style={{ display: 'block' }}
    >
      <g fill="currentColor">
        {ys.map((y) => (
          <g key={y}>
            <circle cx={left} cy={y} r={r} />
            <circle cx={right} cy={y} r={r} />
          </g>
        ))}
      </g>
    </svg>
  );
}
